/* eslint-disable react-hooks/exhaustive-deps */
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Icon,
  SearchInput,
  Space,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { CloseSIcon } from '@tonic-one/react-icons';
import { useToggle } from '@tonic-one/react-hooks';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DelModal from 'src/pages/Report/ActionModal/del';

import { GlobalApi } from '@Apis';
import ErrorAlert from '../../../components/Common/ErrorAlert';
import { useDisclosure } from '../../../components/Common/Tooltip/TextEllipsis/utils';
import CSpinner from '../../../components/CSpinner';
import IconButton from '../../../components/IconButton';
import config from '../../../config';
import { DNSExceptionProps } from '../../../types/globalSetting';
import LoadingBtn from '../../Report/LoadingBtn';
import SmartTooltip from '../../Report/SmartTooltip';
import { AddModal } from '../Common/AddModal';
import { usePermissionStore } from '@Models/usePermissionModel';
import { PermissionNameEnum } from '@Types/permissions';
const DNSDetectionExceptionList = () => {
  const { widthEditablePermission } = usePermissionStore();
  const withEditPermission = widthEditablePermission(
    PermissionNameEnum.administrator
  );
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [loading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation();
  const [items, setItems] = useState<string[]>([]);
  const [allItems, setAllItems] = useState<string[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const [delModalTitle, setDelModalTitle] = useState<ReactNode>();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [delModalContent, setDelModalContent] = useState<
    JSX.Element[] | undefined
  >(undefined);
  const [isAddModalOpen, toggleAddModalOpen] = useToggle(false);
  const [delLoading, setDelLoading] = useState(false);
  const search = (keyword: string) => {
    if (keyword !== '') {
      const temp = allItems?.filter(
        (item) => item.toLowerCase().indexOf(keyword.toLowerCase()) > -1
      );
      setItems(temp);
    } else {
      setItems(allItems);
    }
  };
  const hoverBackgroundColor = (
    {
      dark: 'rgba(255, 255, 255, 0.12)',
      light: 'rgba(0, 0, 0, 0.12)',
    } as any
  )[colorMode];

  async function loadData() {
    try {
      setLoading(true);
      const data: DNSExceptionProps = (await GlobalApi.getDNSExceptionList())
        .data;
      setItems(data.users);
      setAllItems(data.users);
    } catch (error: any) {
      setErrorMsg(t('common.apiException.internalError'));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    (async function doCall() {
      await loadData();
    })();
  }, []);

  const columns: ColumnDef<string>[] = useMemo(
    () => [
      {
        id: 'selection',
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            indeterminate={row.getIsSomeSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
        size: 48,
      },
      {
        id: 'email',
        header: t(
          'globalSetting.DNSDetectionExceptionList.column.emailAddress'
        ),
        cell: ({ row }) => {
          return row.original;
        },
        size: 1000,
      },
    ],
    []
  );
  const [rowSelection, setRowSelection] = useState({});
  const table = useReactTable({
    data: items,
    columns,
    defaultColumn: {
      minSize: 40,
    },
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row,
    state: {
      rowSelection,
    },
    enableRowSelection: true, // enable row selection for all rows
    // enableRowSelection: row => row.original.detections > 0, // or enable row selection conditionally per row
    onRowSelectionChange: setRowSelection,
  });
  const cellHeight = 36;
  const headerHeight = 36;
  const rowsToDisplay = 10;
  const tableStyleProps = {
    height: headerHeight + rowsToDisplay * cellHeight,
    // Uncomment the following line to specify the width and enable horizontal scrollbar
    // width: 200,
  };
  const selectedRowCount = Object.keys(rowSelection).length;
  const handleDel = () => {
    setDelModalTitle(
      t('globalSetting.DNSDetectionExceptionList.alertModal.delete.title')
    );
    const selectedRows = getSelectedRows();
    const content = selectedRows.map((item) => (
      <Text key={item} mb={8}>
        {item}
      </Text>
    ));
    setDelModalContent(content);
    onOpen();
  };
  const reset = () => {
    setRowSelection({});
    search('');
  };

  function getSelectedRows() {
    const selectedRows: string[] = [];
    for (const key of Object.keys(rowSelection)) {
      const item = items.find((item) => item === key);
      if (item) {
        selectedRows.push(item);
      }
    }
    return selectedRows;
  }

  const onAdd = () => {
    toggleAddModalOpen();
  };

  const handleAdd = async (value: string) => {
    try {
      if (value) {
        const res = await GlobalApi.addDNSExceptionList({ users: [value] });
        if (res.data) {
          window.showNotification({
            titleMsg: t('globalSetting.DNSDetectionExceptionList.add.success'),
            severity: 'success',
          });
        }
      }
    } catch (error: any) {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      toggleAddModalOpen();
      await loadData();
    }
  };

  const confirmDel = async () => {
    setDelLoading(true);
    if (!selectedRowCount) return;
    try {
      const selectedRows = getSelectedRows();
      const res = await GlobalApi.deleteDNSExceptionList({
        users: selectedRows,
      });
      if (res.data?.deleteCount) {
        reset();
        window.showNotification({
          titleMsg: t('globalSetting.DNSDetectionExceptionList.delete.success'),
          severity: 'success',
        });
      }
    } catch (error: any) {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    } finally {
      onClose();
      setDelLoading(false);
      await loadData();
    }
  };

  return (
    <Box px="4x">
      {loading && <CSpinner />}
      <Box>
        <DelModal
          visible={isOpen}
          onClose={onClose}
          onConfirm={confirmDel}
          loading={delLoading}
          title={delModalTitle}
          content={delModalContent}
          withEditPermission={withEditPermission}
        ></DelModal>
        <AddModal
          visible={isAddModalOpen}
          allData={allItems}
          onClose={toggleAddModalOpen}
          onSave={handleAdd}
          title={t('globalSetting.DNSDetectionExceptionList.addModal.title')}
        />
        {errorMsg && (
          <ErrorAlert
            errorMsg={errorMsg}
            onClose={() => setErrorMsg(undefined)}
          />
        )}
        <Flex m="16px 0px">
          <Text fontSize="lg" lineHeight="lg" color={colorStyle.color.primary}>
            {t('globalSetting.DNSDetectionExceptionList.tab.title')}
          </Text>
        </Flex>
        <Flex m="16px 0px">
          <Text
            color={colorStyle.color.secondary}
            lineHeight="lg"
            maxWidth={1000}
          >
            {t('globalSetting.DNSDetectionExceptionList.description', {
              count:
                config.ui.globalSettings.limitation
                  .displayNameSpoofingDetectionExceptionListMaxCount,
            })}
          </Text>
        </Flex>
        <Flex m="16px 0px">
          <Text>
            <Space>{t('globalSetting.note.title')}</Space>
            <Space width="1x"></Space>
            <Space color={colorStyle.color.secondary}>
              {t('globalSetting.note.applyToAllOrganizations')}
            </Space>
          </Text>
        </Flex>
        <Flex alignItems="center">
          {selectedRowCount !== 0 ? (
            <Box mr={16}>
              <Stack direction="row">
                <LoadingBtn
                  variant="secondary"
                  mr="4x"
                  onClick={handleDel}
                  disabled={!withEditPermission}
                >
                  {t('reportCommon.buttons.delete')}
                </LoadingBtn>
                <IconButton onClick={reset}>
                  <Icon as={CloseSIcon}></Icon>
                </IconButton>
                <Box lineHeight="2rem">
                  <Text color={colorStyle.color.tertiary}>
                    {selectedRowCount}&nbsp;
                    {t('common.label.selected')}
                  </Text>
                </Box>
              </Stack>
            </Box>
          ) : null}
          {selectedRowCount === 0 ? (
            <Stack direction="row">
              <Button
                variant="primary"
                onClick={onAdd}
                disabled={
                  !withEditPermission ||
                  allItems.length >=
                    config.ui.globalSettings.limitation
                      .displayNameSpoofingDetectionExceptionListMaxCount
                }
              >
                {t('common.action.label.add')}
              </Button>
              <Space width="12px" />
              <SearchInput
                placeholder={t(
                  'globalSetting.DNSDetectionExceptionList.column.emailAddress'
                )}
                onChange={(e: any) => {
                  search(e.target.value);
                }}
                onClearInput={() => {
                  search('');
                }}
                width="374px"
              />
            </Stack>
          ) : null}
        </Flex>
        <Table mt={28} layout="flexbox" {...tableStyleProps}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableHeaderRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const styleProps = {
                    minWidth: header.column.columnDef.minSize,
                    width: header.getSize(),
                  };
                  return (
                    <TableHeaderCell key={header.id} {...styleProps}>
                      {header.isPlaceholder ? null : (
                        <SmartTooltip>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </SmartTooltip>
                      )}
                    </TableHeaderCell>
                  );
                })}
              </TableHeaderRow>
            ))}
          </TableHeader>
          <TableBody overflowY="scroll">
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                _hover={{
                  backgroundColor: hoverBackgroundColor,
                }}
              >
                {row.getVisibleCells().map((cell) => {
                  const styleProps = {
                    minWidth: cell.column.columnDef.minSize,
                    width: cell.column.getSize(),
                  };
                  return (
                    <TableCell key={cell.id} {...styleProps}>
                      <SmartTooltip>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </SmartTooltip>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};
export default DNSDetectionExceptionList;
